import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';
import NoticeDetailsModal from './parts/NoticeDetailsModal';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.14)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  bg:          '#f7f8fa',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
};

const BROADCAST_META: Record<string, { color: string; bg: string; border: string; icon: string; label: string }> = {
  branch:       { color: C.green, bg: C.greenDim, border: C.greenBorder, icon: 'pi-building',   label: 'Branch' },
  multi_branch: { color: '#a06000', bg: C.goldDim, border: C.goldBorder, icon: 'pi-sitemap',     label: 'Multi-Branch' },
  batch:        { color: C.red,   bg: C.redDim,   border: C.redBorder,   icon: 'pi-users',       label: 'Batch' },
};
const defaultBroadcastMeta = { color: C.navy, bg: C.navyHover, border: 'rgba(14,34,93,0.2)', icon: 'pi-megaphone', label: 'Notice' };
const getBroadcastMeta = (type: string) => BROADCAST_META[type] ?? defaultBroadcastMeta;

/* ─── Types ─────────────────────────────────────────────────────────── */
interface Notice {
  notice_id:      string;
  notice_title:   string;
  notice_body:    string;
  created_at:     string;
  broadcast_type: string;
}

interface ApiResponse {
  success:    boolean;
  data:       Notice[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ─── Component ─────────────────────────────────────────────────────── */
const NoticeBoard: React.FC = () => {
  const [notices,      setNotices]      = useState<Notice[]>([]);
  const [loading,       setLoading]      = useState(true);
  const [error,         setError]        = useState<string | null>(null);
  const [page,          setPage]         = useState(1);
  const [limit,         setLimit]        = useState(10);
  const [pagination,    setPagination]   = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  // Modal state
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);
  const [modalVisible,     setModalVisible]     = useState(false);

  const { profile } = useStudentProfileStore();
  const branchId = profile?.branch?.branch_id ?? null;

  useEffect(() => {
    if (branchId) fetchNotices();
  }, [page, limit, branchId]);

  const fetchNotices = async () => {
    if (!branchId) {
      setError('Branch information not available');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ page: String(page), limit: String(limit) });

      const res = await apiClient.get<ApiResponse>(`/v1/noticeboard/by-branch/${branchId}?${params}`);
      if (res.data.success) {
        setNotices(res.data.data);
        setPagination(res.data.pagination);
      } else {
        setError('Failed to load notices');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleNoticeClick = (notice: Notice) => {
    setSelectedNoticeId(notice.notice_id);
    setModalVisible(true);
  };

  const handleModalHide = () => {
    setModalVisible(false);
    setSelectedNoticeId(null);
  };

  /* ── Cell renderers ─────────────────────────────────────────────── */
  const typeTemplate = (row: Notice) => {
    const meta = getBroadcastMeta(row.broadcast_type);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '20px', fontSize: '0.71rem', fontWeight: '700', background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <i className={`pi ${meta.icon}`} style={{ fontSize: '0.68rem' }} />
        {meta.label}
      </span>
    );
  };

  const titleTemplate = (row: Notice) => (
    <div>
      <div style={{ fontWeight: '700', color: C.navy, fontSize: '0.84rem', marginBottom: '2px' }}>{row.notice_title}</div>
      {row.notice_body && (
        <div style={{
          fontSize: '0.76rem', color: C.sub, maxWidth: '360px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {row.notice_body}
        </div>
      )}
    </div>
  );

  const dateTemplate = (row: Notice) => (
    <div style={{ fontSize: '0.82rem', color: C.body, fontWeight: '600' }}>
      <i className="pi pi-clock" style={{ fontSize: '0.7rem', marginRight: '4px', color: C.muted }} />
      {formatDate(row.created_at)}
    </div>
  );

  const actionTemplate = (row: Notice) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      <button
        onClick={() => handleNoticeClick(row)}
        title="View"
        style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, transition: 'all 0.15s ease' }}
        onMouseEnter={e => { e.currentTarget.style.background = C.navyHover; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; e.currentTarget.style.color = C.navy; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.78rem' }} />
      </button>
    </div>
  );

  /* ── Pagination ─────────────────────────────────────────────────── */
  const renderPagination = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }
    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{ minWidth: '32px', height: '32px', padding: '0 8px', borderRadius: '7px', fontSize: '0.82rem', fontWeight: page === p ? '700' : '500', background: page === p ? C.navy : 'transparent', color: page === p ? C.gold : C.navy, border: page === p ? 'none' : `1px solid ${C.border}`, boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none', cursor: 'pointer', transition: 'all 0.15s ease' }}
          onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  /* ── Loading skeleton ───────────────────────────────────────────── */
  if (loading && notices.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading notices…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .nb-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .nb-datatable .p-datatable-tbody > tr { transition: background 0.12s; cursor: pointer; }
        .nb-datatable .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .nb-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>

          {/* ── Toolbar ─────────────────────────────────────────────── */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing</span>
              <select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.8rem', color: C.navy, background: C.white, fontWeight: '600', cursor: 'pointer', outline: 'none' }}
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* ── DataTable ───────────────────────────────────────────── */}
          <DataTable
            value={notices}
            dataKey="notice_id"
            loading={loading}
            onRowClick={e => handleNoticeClick(e.data as Notice)}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-file" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>No notices found</span>
              </div>
            }
            className="nb-datatable"
            style={{ fontSize: '0.83rem' }}
          >
            <Column header="Title / Body" body={titleTemplate} headerStyle={{ minWidth: '280px' }} />
            <Column header="Type"         body={typeTemplate}  headerStyle={{ minWidth: '130px' }} />
            <Column header="Date"         body={dateTemplate}  headerStyle={{ minWidth: '140px' }} />
            <Column header="Actions" body={actionTemplate} headerStyle={{ width: '80px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* ── Pagination footer ───────────────────────────────────── */}
          {pagination.pages > 0 && (
            <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} notices
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPagination()}

                <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <NoticeDetailsModal
        visible={modalVisible}
        noticeId={selectedNoticeId}
        onHide={handleModalHide}
      />
    </>
  );
};

export default NoticeBoard;